/**
 * Top-left info panel — scene title (always visible) + optional subtitle /
 * description / attribution that expand on click. Ported from
 * webpages/viewer/src/ui/InfoPanel.ts but trimmed: no animated width/height tween,
 * just an opacity reveal of the details — viewer2 scenes don't yet author
 * the longer text fields so a fancy tween isn't earning its complexity.
 */

export interface SceneInfo {
    title?: string;
    subtitle?: string;
    description?: string;
    attribution?: string;
}

export interface InfoPanelOptions {
    parent: HTMLElement;
    info: SceneInfo;
    /** Embed mode: title only, no expand. */
    embed?: boolean;
}

export class InfoPanel {

    private root: HTMLDivElement;

    private details: HTMLDivElement | null = null;

    private expanded = false;

    private embed: boolean;

    private onDocClickBound: (e: MouseEvent) => void;

    constructor({ parent, info, embed = false }: InfoPanelOptions) {
        this.embed = embed;

        this.root = document.createElement('div');
        this.root.className = 'ollo-info-panel';

        if (info.title) {
            const title = document.createElement('div');
            title.className = 'ollo-info-title';
            title.textContent = info.title;
            this.root.appendChild(title);
        }

        const hasDetails = !embed && (info.subtitle || info.description || info.attribution);
        if (hasDetails) {
            const d = document.createElement('div');
            d.className = 'ollo-info-details';
            if (info.subtitle) {
                const s = document.createElement('div');
                s.className = 'ollo-info-subtitle';
                s.textContent = info.subtitle;
                d.appendChild(s);
            }
            if (info.description) {
                const desc = document.createElement('div');
                desc.className = 'ollo-info-description';
                desc.textContent = info.description;
                d.appendChild(desc);
            }
            if (info.attribution) {
                const a = document.createElement('div');
                a.className = 'ollo-info-attribution';
                a.textContent = info.attribution;
                d.appendChild(a);
            }
            this.root.appendChild(d);
            this.details = d;

            this.root.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggle();
            });
        }

        parent.appendChild(this.root);
        this.onDocClickBound = () => { if (this.expanded) this.collapse(); };
    }

    private toggle(): void {
        if (this.embed || !this.details) return;
        if (this.expanded) this.collapse();
        else this.expand();
    }

    private expand(): void {
        this.expanded = true;
        this.root.classList.add('expanded');
        document.addEventListener('click', this.onDocClickBound);
    }

    private collapse(): void {
        this.expanded = false;
        this.root.classList.remove('expanded');
        document.removeEventListener('click', this.onDocClickBound);
    }

    dispose(): void {
        document.removeEventListener('click', this.onDocClickBound);
        this.root.remove();
    }
}
